import { FetchQuestionnaireProfilesResponse, Subject } from "../interface"

import { isStorageAvailable, StorageWrapper } from "utils/infrastructure/storage-util"
import { Psychologist, Sex, SlotType } from "utils/interfaces"

const VERSION = 4
const DATA_KEY_PREFIX = "rs:data"

export interface ResultsDataBase {
    matching_count: number
    profiles: Psychologist[]
    fallback_competences: number[]
    questionnaire_results_id?: number
    reported_problems: string
    reported_problems_id: number[]
    subject: Subject
    price_category?: string
    price_min?: number
    price_max?: number
    sex?: Sex
    title: string
    form_of_work?: SlotType
    extraData?: FetchQuestionnaireProfilesResponse
    slug: string
}

interface DataWithMeta {
    data: ResultsDataBase
    key: string
    timestamp: string
    version: typeof VERSION
}

class ResultsStorage {
    storage: StorageWrapper | undefined
    key: string

    constructor() {
        this.storage = isStorageAvailable ? new StorageWrapper(window.localStorage) : undefined
        this.key = `${DATA_KEY_PREFIX}:${VERSION}`
    }

    store(newData: ResultsDataBase) {
        const record: DataWithMeta = {
            key: this.key,
            data: newData,
            timestamp: new Date().toISOString(),
            version: VERSION,
        }

        this.storage?.storeObject(this.key, record)
        return this.key
    }

    storePartial(partialData: Partial<ResultsDataBase>) {
        const existing = this.get()

        const updated: ResultsDataBase = {
            ...existing,
            ...partialData,
        }

        return this.store(updated)
    }

    get(): ResultsDataBase {
        const data = this.storage?.getObject<DataWithMeta>(this.key)

        if (data && data.data) {
            return data.data
        }

        return {
            matching_count: 0,
            profiles: [],
            fallback_competences: [],
            questionnaire_results_id: 0,
            reported_problems: "",
            reported_problems_id: [],
            subject: Subject.Myself,
            title: "",
            slug: "",
            price_category: "",
        }
    }

    get matchingCount(): number {
        return this.get().matching_count
    }

    get profiles(): Psychologist[] {
        return this.get().profiles
    }

    get fallbackCompetences(): number[] {
        return this.get().fallback_competences
    }

    get resultsId(): number | undefined {
        return this.get().questionnaire_results_id
    }

    get reportedProblems(): string {
        return this.get().reported_problems
    }

    get reportedProblemsId(): number[] {
        return this.get().reported_problems_id
    }

    get subject(): Subject {
        return this.get().subject
    }

    get priceMin(): number | undefined {
        return this.get().price_min
    }

    get priceMax(): number | undefined {
        return this.get().price_max
    }

    get priceCategory(): string | undefined {
        return this.get().price_category
    }

    get sex(): Sex | undefined {
        return this.get().sex
    }

    get title(): string {
        return this.get().title
    }

    get formOfWork(): SlotType | undefined {
        return this.get().form_of_work
    }

    get slug(): string {
        return this.get().slug
    }

    get extraData(): FetchQuestionnaireProfilesResponse | undefined {
        return this.get().extraData
    }

    clean() {
        this.storage?.remove(this.key)
    }
}

export const SurveyResultsStorage = new ResultsStorage()
